'use client';

import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	calculateLongShortFundingRateAndLiveTwaps,
	calculateTerminalPrice,
	convertToNumber,
	FUNDING_RATE_BUFFER_PRECISION,
	FUNDING_RATE_PRECISION_EXP,
	MarketStatus,
	OracleSource,
	PEG_PRECISION,
	PRICE_PRECISION,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS } from '@velocity-exchange/common';
import {
	useEffect,
	useMemo,
	useRef,
	createContext,
	useContext,
	PropsWithChildren,
} from 'react';
import { useImmer } from 'use-immer';
import Env from '../environmentVariables/EnvironmentVariables';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useMarketStateStore from '../stores/useMarketStateStore';
import useDriftClientIsReady from './useDriftClientIsReady';
import useInfoForCurrentlySelectedMarket from './useInfoForCurrentlySelectedMarket';
import useMarkPrice from './useMarkPrice';
import { pricingDataToOracleBigNum } from './query/pricing/utils';
import { getFundingRatePct } from 'src/utils/fundingRate';
import useGetRawOracleDataForMarket from './useGetRawOracleDataForMarket';
import { usePricingData } from './query/pricing/usePricingData';

export type MarketData = {
	baseAssetSymbol: string;
	symbol: string;
	oraclePrice: number;
	mmOraclePrice?: number;
	oracleErr: string;
	mmOracleErr?: string;
	indexTwap: string;
	indexTwapErr: string;
	twapSpreadPct: string;

	totalFee: string;
	totalFeeMinusDistributions: string;
	cumLongRepegProfit: string;
	cumShortRepegProfit: string;
	cumLongFundingRate: string;
	cumShortFundingRate: string;

	baseAssetAmountLong: string;
	baseAssetAmountShort: string;

	fundingRate: string;
	longFundingRate: string;
	shortFundingRate: string;

	lastFundingRate: string;
	estimatedFundingRateCountdownTs: number;
	lastFundingRateTime: string;

	baseVolume: string;
	quoteVolume: string;
	twap: string;
	maxOpenInterest: string;
	openInterest: string;
	longOpenInterest: string;
	shortOpenInterest: string;
	pegMultiplier: string;
	periodicity: string;

	baseAssetAmount: string;
	quoteAssetAmount: string;
	unPeggedQuoteAssetAmount: string;
	bias: string;
	cumFundingRate: string;

	feePoolSize: string;
	terminalPrice: string;
	oracleTwap: string;

	markTwapLive: string;
	oracleTwapLive: string;
	oracleSource: OracleSource;
	lastOracleSlotDelay: number;
	lastMmOracleSlotDelay: number;
	currentSlot: number;

	hasLoadedInitialData: boolean;
};

const BASE_STATE: MarketData = {
	baseAssetSymbol: '',
	symbol: '',
	oraclePrice: 0,
	mmOraclePrice: 0,
	fundingRate: `0.00`,
	longFundingRate: `0.00`,
	shortFundingRate: `0.00`,
	estimatedFundingRateCountdownTs: 0,
	lastFundingRateTime: `0`,
	baseVolume: `0`,
	lastFundingRate: `0`,
	quoteVolume: `0`,
	twap: `0`,
	maxOpenInterest: '0',
	openInterest: `0`,
	longOpenInterest: `0`,
	shortOpenInterest: `0`,
	pegMultiplier: `0`,
	periodicity: `0`,
	baseAssetAmount: `0`,
	quoteAssetAmount: `0`,
	unPeggedQuoteAssetAmount: `0`,
	baseAssetAmountLong: `0`,
	baseAssetAmountShort: `0`,
	totalFee: `0`,
	totalFeeMinusDistributions: `0`,
	bias: `0`,
	cumFundingRate: `0`,
	indexTwap: `0`,
	oracleErr: `0`,
	indexTwapErr: `0`,
	feePoolSize: `0`,
	twapSpreadPct: `0`,
	cumLongRepegProfit: `0`,
	cumShortRepegProfit: `0`,
	cumLongFundingRate: `0`,
	cumShortFundingRate: `0`,
	oracleTwap: `0`,
	markTwapLive: `0`,
	oracleTwapLive: `0`,
	terminalPrice: `0`,
	oracleSource: OracleSource.PYTH,
	lastOracleSlotDelay: 0,
	lastMmOracleSlotDelay: 0,
	currentSlot: 0,
	hasLoadedInitialData: false,
};

const LATE_FUNDING_CUTOFF_PERIOD = 60 * 20;

const calculateNextFundingPaymentTs = (
	now: number,
	periodicityNum: number,
	lastFundingTsNum: number
) => {
	let nextFundingTs: number;

	// Determine when next funding is expected
	//// https://docs.drift.trade/funding-rates

	const periodStartForTs = (ts: number) => ts - (ts % periodicityNum);

	const nextFundingEpoch = periodStartForTs(now) + periodicityNum;

	const lastFundingWasLate =
		lastFundingTsNum % periodicityNum > LATE_FUNDING_CUTOFF_PERIOD;

	const lastFundingWasPaidDuringCurrentPeriod =
		periodStartForTs(lastFundingTsNum) === periodStartForTs(now);

	// Check if funding is due
	let fundingIsCurrentlyDue = false;

	//// Case 1 : Last funding was not late, but missed the following payment period
	if (!lastFundingWasLate && !lastFundingWasPaidDuringCurrentPeriod) {
		fundingIsCurrentlyDue = true;
	}

	//// Case 2 : Last funding was late, but missed the skipped payment period
	if (lastFundingWasLate && !lastFundingWasPaidDuringCurrentPeriod) {
		const nextFundingShouldBePaidAt =
			periodStartForTs(lastFundingTsNum) + periodicityNum * 2;

		if (nextFundingShouldBePaidAt <= now) {
			fundingIsCurrentlyDue = true;
		}
	}

	// # Set the expected next funding timestamp
	if (lastFundingWasLate && lastFundingWasPaidDuringCurrentPeriod) {
		nextFundingTs = nextFundingEpoch + periodicityNum;
	} else if (fundingIsCurrentlyDue) {
		nextFundingTs = periodStartForTs(now);
	} else {
		nextFundingTs = nextFundingEpoch;
	}

	return nextFundingTs;
};

// Add context and provider after the BASE_STATE definition
const MarketInfoDisplayDataContext = createContext<MarketData>(BASE_STATE);

export const MarketInfoDisplayDataProvider = ({
	children,
}: PropsWithChildren) => {
	// # State
	const selectedMarketInfo = useInfoForCurrentlySelectedMarket();
	const selectedMarketId = selectedMarketInfo?.info?.marketId;
	const marketIsPerp = selectedMarketId?.isPerp;
	const [marketData, setMarketData] = useImmer<MarketData>(BASE_STATE);
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const bulkAccountLoader = useDriftStore((s) => s.connection.accountLoader);
	const markPrice = useMarkPrice(selectedMarketId);

	// Oracle price from WebSocket-backed pricing data (reactive)
	const marketKey = useMemo(
		() => selectedMarketId?.key,
		[selectedMarketId?.marketIndex, selectedMarketId?.marketType]
	);
	const { data: pricingData } = usePricingData(marketKey);
	const oracleDisplayPrice = pricingDataToOracleBigNum(pricingData);

	// Raw oracle data (SDK OraclePriceData with BN values) for confidence/twap/slot/maxPrice
	const getRawOracleDataForMarket = useGetRawOracleDataForMarket();
	const rawOracleData = selectedMarketId
		? getRawOracleDataForMarket(selectedMarketId)
		: undefined;

	// TODO: move to react query
	const mmOraclePriceData = useMarketStateStore((s) =>
		selectedMarketId ? s.getMmOracleDataForMarket(selectedMarketId) : undefined
	);
	// TODO: move to react query
	const rawMmOraclePriceData = useMarketStateStore((s) =>
		selectedMarketId
			? s.getRawMmOracleDataForMarket(selectedMarketId)
			: undefined
	);
	const lastFundingTsEstimateUpdate = useRef<number>(0);
	const lastFundingTsMarket = useRef<number>(undefined);

	// # Effect Hooks
	useEffect(() => {
		if (!driftClientIsReady) {
			setMarketData(BASE_STATE);
			return;
		}
	}, [driftClientIsReady]);

	// Effect to keep info in sync with oracle and market
	useEffect(() => {
		if (!selectedMarketInfo || !driftClient) return;

		const nowBN = new BN((Date.now() / 1000).toFixed(0));
		const now = nowBN.toNumber();
		const nowSlot = bulkAccountLoader?.mostRecentSlot || 0;

		const market = driftClient.getPerpMarketAccount(
			selectedMarketId?.marketIndex ?? 0
		);

		const lastOracleSlot = rawOracleData?.slot?.toNumber() ?? 0;
		const lastMmOracleSlot = mmOraclePriceData?.slot?.toNumber() ?? 0;

		// todo - refactor to pull marketaccount for either perp/spot
		if (!marketIsPerp) {
			setMarketData((s) => {
				s.hasLoadedInitialData =
					marketData.oraclePrice !== BASE_STATE.oraclePrice;
				s.oracleSource =
					selectedMarketInfo?.info?.config?.oracleSource ??
					BASE_STATE.oracleSource;
				s.lastOracleSlotDelay = nowSlot - lastOracleSlot;
				s.lastMmOracleSlotDelay = nowSlot - lastMmOracleSlot;
			});
			return;
		}

		if (!market || ENUM_UTILS.match(market.status, MarketStatus.DELISTED)) {
			setMarketData(BASE_STATE);
			return;
		}

		const lastFundingTs = market.lastFundingRateTs;
		const lastFundingRate = market.lastFundingRate;
		const periodicity = market.marketStats.fundingPeriod;

		if (!lastFundingTs || !periodicity) {
			setMarketData(BASE_STATE);
			return;
		}

		const periodicityNum = periodicity.toNumber();
		const lastFundingTsNum = lastFundingTs.toNumber();

		const nextFundingTs = calculateNextFundingPaymentTs(
			now,
			periodicityNum,
			lastFundingTsNum
		);
		const nextFundingTimeRemaining = Math.max(nextFundingTs - now, 0);

		const markTwapWithMantissa = market.marketStats.lastMarkPriceTwap;

		const twapSpreadPct = markTwapWithMantissa
			.sub(market.marketStats.lastMarkPriceTwap)
			.mul(PRICE_PRECISION)
			.mul(new BN(100))
			.div(BN.max(new BN(1), market.marketStats.lastMarkPriceTwap));

		setMarketData((s) => {
			s.baseAssetSymbol =
				selectedMarketInfo.info?.genericInfo?.baseAssetSymbol ?? '';
			s.symbol = selectedMarketInfo.info?.genericInfo?.symbol ?? '';
			s.terminalPrice = convertToNumber(calculateTerminalPrice(market)).toFixed(
				3
			);
			s.maxOpenInterest = BigNum.from(
				market.maxOpenInterest,
				BASE_PRECISION_EXP
			).toMillified();
			s.openInterest = BigNum.from(
				market.baseAssetAmountLong.add(market.baseAssetAmountShort.abs()),
				BASE_PRECISION_EXP
			).toMillified();
			s.longOpenInterest = BigNum.from(
				market.baseAssetAmountLong,
				BASE_PRECISION_EXP
			).toMillified();
			s.shortOpenInterest = BigNum.from(
				market.baseAssetAmountShort.abs(),
				BASE_PRECISION_EXP
			).toMillified();
			s.oracleTwap = BigNum.from(
				market.marketStats.lastMarkPriceTwap,
				QUOTE_PRECISION_EXP
			).toFixed(3);

			s.totalFee = BigNum.from(
				market.amm.totalFee,
				QUOTE_PRECISION_EXP
			).prettyPrint();
			s.totalFeeMinusDistributions = BigNum.from(
				market.amm.totalFeeMinusDistributions,
				QUOTE_PRECISION_EXP
			).prettyPrint();
			s.feePoolSize = BigNum.from(
				market.amm.totalFeeMinusDistributions.sub(
					market.amm.totalFee.div(new BN(2))
				),
				QUOTE_PRECISION_EXP
			).prettyPrint();
			s.bias = BigNum.from(
				market.amm.baseAssetAmountWithAmm,
				BASE_PRECISION_EXP
			).toString();
			s.baseAssetAmount = BigNum.from(
				market.amm.baseAssetReserve,
				BASE_PRECISION_EXP
			).print();
			s.quoteAssetAmount = BigNum.from(
				market.amm.quoteAssetReserve,
				BASE_PRECISION_EXP
			).print();
			s.unPeggedQuoteAssetAmount = BigNum.from(
				market.amm.quoteAssetReserve.mul(market.amm.pegMultiplier),
				BASE_PRECISION_EXP
			)
				.div(PEG_PRECISION)
				.toString();
			s.baseAssetAmountLong = BigNum.from(
				market.baseAssetAmountLong,
				BASE_PRECISION_EXP
			).toString();
			s.baseAssetAmountShort = BigNum.from(
				market.baseAssetAmountShort,
				BASE_PRECISION_EXP
			).toString();

			s.twap = BigNum.from(
				market.marketStats.lastMarkPriceTwap,
				QUOTE_PRECISION_EXP
			).toFixed(3);
			s.pegMultiplier =
				(
					market.amm.pegMultiplier.toNumber() / PEG_PRECISION.toNumber()
				).toLocaleString(Env.locale) ?? 'N/A';
			s.periodicity = periodicity.toString() ?? 'N/A';
			s.twapSpreadPct =
				BigNum.from(twapSpreadPct, QUOTE_PRECISION_EXP).toFixed(5) ?? 'N/A';
			s.oracleSource = market.oracleSource;

			s.cumLongFundingRate = BigNum.from(
				market.cumulativeFundingRateLong.div(FUNDING_RATE_BUFFER_PRECISION),
				QUOTE_PRECISION_EXP
			).prettyPrint();
			s.cumShortFundingRate = BigNum.from(
				market.cumulativeFundingRateShort.div(FUNDING_RATE_BUFFER_PRECISION),
				QUOTE_PRECISION_EXP
			).prettyPrint();

			s.lastFundingRate = getFundingRatePct(lastFundingRate).printShort();
			s.lastFundingRateTime = lastFundingTs.toString() ?? 'N/A';

			if (nowSlot > 0) {
				s.lastOracleSlotDelay = nowSlot - lastOracleSlot;
				s.lastMmOracleSlotDelay = nowSlot - lastMmOracleSlot;
				s.currentSlot = nowSlot;
			}

			// this updates every second, since mark price (a dependency of this useEffect) is updated every second for the current market
			// important to note that this hook references the current market instead of any other markets (whose mark price may be updated less frequently)
			s.estimatedFundingRateCountdownTs = nextFundingTimeRemaining;
		});

		if (!rawOracleData) {
			setMarketData((s) => {
				s.hasLoadedInitialData = true;
			});
			return;
		}

		const [markTwapLive, oracleTwapLive, longFundingRate, shortFundingRate] =
			calculateLongShortFundingRateAndLiveTwaps(
				market,
				rawMmOraclePriceData,
				rawOracleData,
				markPrice.val,
				nowBN
			);

		const fundingRateStr = driftClientIsReady
			? BigNum.from(longFundingRate as BN, FUNDING_RATE_PRECISION_EXP)
					.toFixed(5)
					.toString() +
				'%/' +
				BigNum.from(shortFundingRate as BN, FUNDING_RATE_PRECISION_EXP)
					.toFixed(5)
					.toString()
			: 'N/A';

		setMarketData((s) => {
			s.markTwapLive = BigNum.from(markTwapLive, QUOTE_PRECISION_EXP).toFixed(
				3
			);
			s.oracleTwapLive = BigNum.from(
				oracleTwapLive,
				QUOTE_PRECISION_EXP
			).toFixed(3);
			s.fundingRate = fundingRateStr;
			s.longFundingRate = getFundingRatePct(longFundingRate).printShort();
			s.shortFundingRate = getFundingRatePct(shortFundingRate).printShort();

			// If we haven't updated the funding estimate for 5 minutes, or the new estimate and the currently running estimate are more than 5 seconds out of sync, then update the current running estimate
			if (
				lastFundingTsMarket.current === undefined ||
				lastFundingTsMarket.current !== market.marketIndex ||
				!lastFundingTsEstimateUpdate.current ||
				Date.now() - lastFundingTsEstimateUpdate.current > 1000 * 60 * 5 ||
				Math.abs(s.estimatedFundingRateCountdownTs - nextFundingTimeRemaining) >
					5
			) {
				s.estimatedFundingRateCountdownTs = nextFundingTimeRemaining;
				lastFundingTsEstimateUpdate.current = Date.now();
				lastFundingTsMarket.current = market.marketIndex;
			}

			s.hasLoadedInitialData = true;
		});
	}, [
		driftClient,
		marketIsPerp,
		selectedMarketInfo?.info?.marketId?.key,
		markPrice.toNum(),
		driftClientIsReady,
		bulkAccountLoader,
		rawOracleData?.slot,
	]);

	// Set the oracle price when pricing data updates or selected market updates
	useEffect(() => {
		const decimalsForMarket =
			selectedMarketInfo?.info?.genericInfo?.tickSizeExponent ?? 2;

		const indexPrice = Number(
			oracleDisplayPrice?.toFixed?.(decimalsForMarket) ?? 0
		);
		// TODO: move to react query
		const mmOraclePrice = Number(
			mmOraclePriceData?.price?.toFixed?.(decimalsForMarket) ?? 0
		);

		const indexErr =
			BigNum.from(
				rawOracleData?.confidence ?? ZERO,
				PRICE_PRECISION_EXP
			)?.toFixed?.(decimalsForMarket) ?? '0';

		const mmOracleErr =
			BigNum.from(
				mmOraclePriceData?.confidence ?? ZERO,
				PRICE_PRECISION_EXP
			)?.toFixed?.(decimalsForMarket) ?? '0';

		const indexTwap = rawOracleData?.twap
			? BigNum.from(rawOracleData.twap, PRICE_PRECISION_EXP)
			: undefined;

		const indexTwapErr = rawOracleData?.twapConfidence;

		setMarketData((s) => {
			if (indexErr) {
				s.oracleErr = indexErr;
			}
			if (mmOracleErr) {
				s.mmOracleErr = mmOracleErr;
			}
			if (indexTwap) {
				s.indexTwap = indexTwap.toFixed(3);
			}
			if (indexTwapErr) {
				s.indexTwapErr = indexTwapErr.toNumber().toFixed(3);
			}

			s.oraclePrice = indexPrice;
			s.mmOraclePrice = mmOraclePrice;
			s.hasLoadedInitialData = true;
		});
	}, [
		selectedMarketInfo?.info?.marketId?.key,
		oracleDisplayPrice?.toNum(),
		rawOracleData?.slot?.toNumber(),
		mmOraclePriceData?.slot?.toNumber(),
	]);

	return (
		<MarketInfoDisplayDataContext.Provider value={marketData}>
			{children}
		</MarketInfoDisplayDataContext.Provider>
	);
};

/**
 * Note : THIS RETURNS MARKET DATA BASED ON THE LIVE DATA BEING FETCHED FROM THE ORACLE. IT SHOULD ONLY BE USED IN UI DISPLAY BUT NOT IN TRANSACTION CREATION, WE USE THE PRICE DATA IN THE DLOB STORE FOR THAT BECAUSE WE ARE SURE THAT THE DATA IS BEING LOADED FROM A CONSISTENT SOURCE
 * @returns
 */
const useMarketInfoDisplayData = () => {
	const context = useContext(MarketInfoDisplayDataContext);
	if (context === undefined) {
		throw new Error(
			'useMarketInfoDisplayData must be used within a MarketInfoDisplayDataProvider'
		);
	}
	return context;
};

export default useMarketInfoDisplayData;
